import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { formatPln } from '../catalog/money';
import { formatDecimal } from '../ui/numbers';
import { ConflictNotice } from '../ui/ConflictNotice';
import { bpToPct, QUOTES_KEY, type QuoteLine, type Version } from './types';
import { DialogLayer } from '../ui/Dialog';

/**
 * Repeated lines of a draft (owner 2026-10-23 20:44: "wolałbym żeby zostały dwie z możliwością połączenia ich … żeby
 * wyświetliło się najpierw które scalić do odhaczenia"). Adding never merges; "…" → "Scal powtórzenia (N)…" opens a
 * list of the groups (the same gear in one section), each ticked by default, and "Scal zaznaczone" merges only the
 * ticked ones — in one request; a group that differs keeps the first line's values (as "Scal mimo to" did).
 */

/**
 * What differs in a group (the server's `MergeReason`): its fields, `net` — the total would change by the rounding of
 * each line (review I1; `netDeltaGrosze` only for whoever sees the amounts) — `description` (joined too long: never
 * merged), `quantity` (the sum beyond the limit), `other` (fields the role does not see, e.g. the company's costs).
 */
export type MergeDiffField = 'name' | 'unitPriceGrosze' | 'daysMilli' | 'discountBp' | 'linear' | 'day' | 'costGrosze' | 'costRateGrosze' | 'net' | 'description' | 'quantity' | 'other';
/** A group as `GET …/duplicates` lists it; `quantity` (after merging) only for whoever sees quantities. */
export type DuplicateGroup = { itemId: string; sectionId: string; differs: MergeDiffField[]; netDeltaGrosze?: number; quantity?: number; mergeable: boolean; lines: QuoteLine[] };
export type MergeResult = { merged: number; removed: number; version: Version };

const DIFF_LABELS: Record<MergeDiffField, string> = {
  name: 'nazwa',
  unitPriceGrosze: 'cena',
  daysMilli: 'dni',
  discountBp: 'rabat',
  linear: 'liczenie dni',
  day: 'data',
  costGrosze: 'koszt firmy',
  costRateGrosze: 'stawka kosztu',
  net: 'kwota (zaokrąglenia)',
  description: 'opis',
  quantity: 'ilość',
  other: 'inne ustawienia',
};

/** "cena, kwota różni się o −0,01 zł" — the amount's difference with its value when the role sees it. */
function reasonsText(g: DuplicateGroup): string {
  return g.differs.map((f) => (f === 'net' && g.netDeltaGrosze !== undefined ? `kwota różni się o ${formatPln(g.netDeltaGrosze)}` : DIFF_LABELS[f])).join(', ');
}

const groups = (n: number) => (n === 1 ? 'grupa' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'grupy' : 'grup');
const rows = (n: number) => (n === 1 ? 'wiersz' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'wiersze' : 'wierszy');

/** The "…" entry, as the packlist's "Scal powtórzenia (N)…" (N = the number of groups). */
export const mergeLabel = (count: number) => `Scal powtórzenia (${count})…`;
export const mergeSummary = (merged: number, removed: number) => `Scalono powtarzające się pozycje: ${merged} ${groups(merged)}, ${removed} ${rows(removed)} mniej.`;

/** One line of a group: its quantity, then the fields that differ (amounts only when the role sees them). */
function lineFacts(l: QuoteLine, differs: readonly MergeDiffField[]): string {
  const facts = [l.quantity !== undefined ? `ilość ${formatDecimal(l.quantity)}` : l.name];
  for (const f of differs) {
    if (f === 'name') facts.unshift(l.name);
    if (f === 'unitPriceGrosze' && l.unitPriceGrosze !== undefined) facts.push(`cena ${formatPln(l.unitPriceGrosze)}`);
    if (f === 'daysMilli' && typeof l.daysMilli === 'number') facts.push(`dni ${formatDecimal(l.daysMilli / 1000)}`);
    if (f === 'discountBp' && l.discountBp !== undefined) facts.push(`rabat ${bpToPct(l.discountBp)}%`);
    if (f === 'linear') facts.push(l.linear ? 'liczone liniowo' : 'liczone wg schematu dni');
    if (f === 'day') facts.push(l.day ? `data ${l.day}` : 'bez daty');
  }
  return facts.join(' · ');
}

const keyOf = (g: DuplicateGroup) => g.lines[0]!.id;

/** Why a group cannot be ticked at all (the server's `mergeable: false`). */
function blockedText(g: DuplicateGroup): string {
  if (g.differs.includes('description')) return 'Nie da się scalić: opisy razem byłyby dłuższe niż 2000 znaków. Skróć któryś opis.';
  if (g.differs.includes('quantity')) return 'Nie da się scalić: ilość albo kwota po scaleniu byłaby za duża.';
  return 'Nie możesz scalić tej grupy: zmieniłoby to pola pozycji, których nie możesz zmieniać.';
}

/**
 * "Scal powtórzenia (N)…": the draft's groups (`GET …/duplicates`), each with its name, section, lines with their
 * quantities, the quantity after merging and — when the lines differ — what differs and that the first line's values
 * stay. All mergeable groups ticked; "Zaznacz wszystkie / Odznacz wszystkie". "Scal zaznaczone" sends the ticked groups
 * (their lines as shown) in one request; a change in the meantime gives 409 — the list is read again. Uses the shared
 * dialog markup, so it sits where the app puts dialogs of the quote's tab; Escape closes only this dialog.
 */
export function MergeDuplicatesDialog({
  versionId,
  sections,
  onMerged,
  onClose,
}: {
  versionId: string;
  sections: ReadonlyArray<{ id: string; name: string }>;
  onMerged: (r: MergeResult) => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const list = useQuery({
    queryKey: [...QUOTES_KEY, 'duplicates', versionId],
    queryFn: () => api<{ groups: DuplicateGroup[] }>(`/api/quotes/versions/${versionId}/duplicates`),
    gcTime: 0,
    staleTime: 0,
  });
  const all = list.data?.groups ?? [];
  const mergeable = all.filter((g) => g.mergeable);
  // ticked by default: every group that may merge; ticks follow a list read again (a new group comes ticked)
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const ticked = mergeable.filter((g) => !unticked.has(keyOf(g)));
  const merge = useMutation({
    mutationFn: (chosen: DuplicateGroup[]) =>
      api<MergeResult>(`/api/quotes/versions/${versionId}/merge-duplicates`, { method: 'POST', body: { groups: chosen.map((g) => g.lines.map((l) => ({ id: l.id, version: l.version }))) } }),
    onSuccess: (r) => {
      onMerged(r);
      onClose();
    },
  });
  const error = merge.error instanceof ApiError ? merge.error : null;
  useEffect(() => {
    if (list.data && !box.current?.contains(document.activeElement)) box.current?.querySelector<HTMLElement>('input[type="checkbox"]:not(:disabled), button')?.focus();
  }, [list.data]);
  const toggle = (g: DuplicateGroup) =>
    setUnticked((u) => {
      const next = new Set(u);
      if (next.has(keyOf(g))) next.delete(keyOf(g));
      else next.add(keyOf(g));
      return next;
    });
  const reload = () => {
    merge.reset();
    void list.refetch();
  };
  const sectionName = (id: string) => sections.find((s) => s.id === id)?.name ?? '';
  return (
    <div className="dialog-backdrop">
      <div ref={box} className="dialog panel q-merge" role="dialog" aria-modal="true" aria-labelledby="q-merge-title" data-feature-id="quotes.merge_lines">
        <DialogLayer onClose={onClose} busy={merge.isPending} />
        <h2 id="q-merge-title">Scal powtórzenia</h2>
        <p>Ten sam sprzęt w tej samej sekcji stoi w kilku wierszach. Zaznacz, które grupy scalić: ilości się zsumują, opisy połączą, zostanie miejsce, nazwa, cena, dni i rabat pierwszego wiersza.</p>
        {list.isLoading && (
          <p className="muted" role="status">
            Wczytywanie…
          </p>
        )}
        {list.error && (
          <p className="alert" role="alert">
            {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać powtórzeń'}
          </p>
        )}
        {error?.status === 409 && <ConflictNotice onReload={reload} />}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        {list.data && all.length === 0 && <p className="hint">Nic się już nie powtarza.</p>}
        {mergeable.length > 1 && (
          <div className="actions">
            <button className="btn small ghost" type="button" disabled={ticked.length === mergeable.length} onClick={() => setUnticked(new Set())}>
              Zaznacz wszystkie
            </button>
            <button className="btn small ghost" type="button" disabled={ticked.length === 0} onClick={() => setUnticked(new Set(mergeable.map(keyOf)))}>
              Odznacz wszystkie
            </button>
          </div>
        )}
        {all.map((g) => {
          const name = g.lines[0]!.name;
          const section = sectionName(g.sectionId);
          const differs = g.differs.filter((d) => d !== 'description' && d !== 'quantity');
          return (
            <fieldset key={keyOf(g)} className="q-merge-group" aria-label={name}>
              <legend>
                <label>
                  <input type="checkbox" checked={g.mergeable && !unticked.has(keyOf(g))} disabled={!g.mergeable || merge.isPending} onChange={() => toggle(g)} /> {name}
                </label>
                {section && <span className="muted"> — sekcja „{section}”</span>}
              </legend>
              <ol>
                {g.lines.map((l) => (
                  <li key={l.id}>{lineFacts(l, g.differs)}</li>
                ))}
              </ol>
              {g.quantity !== undefined && <p className="hint">Po scaleniu: ilość {formatDecimal(g.quantity)}</p>}
              {g.mergeable && differs.length > 0 && <p className="hint">Różni się: {reasonsText({ ...g, differs })}. Zostaną wartości z pierwszego wiersza.</p>}
              {!g.mergeable && <p className="hint">{blockedText(g)}</p>}
            </fieldset>
          );
        })}
        <div className="actions">
          <button className="btn primary" type="button" disabled={ticked.length === 0 || merge.isPending} onClick={() => merge.mutate(ticked)}>
            {merge.isPending ? 'Scalanie…' : 'Scal zaznaczone'}
          </button>
          <button className="btn" type="button" disabled={merge.isPending} onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
